import * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "./lib/utils";

export interface SearchBarProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Current search value */
  value?: string;
  /** Search change handler */
  onValueChange?: (value: string) => void;
  /** Show clear button when value is not empty */
  showClear?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SearchBar - Unified search input with icon and clear button
 *
 * Features:
 * - Search icon (left side)
 * - Clear button when value present
 * - Enhanced focus states
 * - Keyboard shortcuts (Escape to clear)
 * - Accessible with proper ARIA labels
 */
const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  ({ value, onValueChange, onChange, showClear = true, className, placeholder, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange?.(e.target.value);
      onChange?.(e);
    };

    const handleClear = () => {
      onValueChange?.("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape" && value) {
        handleClear();
      }
    };

    return (
      <div className={cn("relative flex-1 max-w-sm", className)}>
        {/* Search icon */}
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />

        {/* Input field */}
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "h-10 w-full rounded-lg border-2 border-gray-300",
            "bg-background pl-10 pr-10 py-2.5 text-sm",
            "placeholder:text-muted-foreground",
            "transition-all",
            "focus:outline-hidden focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10",
            "hover:border-gray-400"
          )}
          aria-label="Search"
          {...props}
        />

        {/* Clear button */}
        {showClear && value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);
SearchBar.displayName = "SearchBar";

export { SearchBar };
